import React, { FC, useState } from "react";
import { Box, Typography, Theme, Divider, useMediaQuery } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { alpha, useTheme } from "@mui/material/styles";
import {
  UserExerciseDto,
  CompletedExercisesWithAssetDto,
  ExerciseAssetDto,
} from "@growth-machine-llc/stridist-api-client";
import { capitalize } from "lodash";
import { TooltipExerciseItem } from "./TooltipExerciseItem";
import { X as Close } from "lucide-react";

export interface TopExerciseItem {
  count: number;
  exercise: UserExerciseDto;
}

export type RadarDataItem = {
  subject: string;
  value: number;
  fullMark: 100;
  rawCount: number;
  relatedExercises: CompletedExercisesWithAssetDto[];
};

export const useTooltipStyles = makeStyles((theme: Theme) => {
  return {
    list: {
      padding: 0,
      margin: 0,
      listStyle: "none",
    },
    listItem: {
      display: "flex",
      alignItems: "center",
      padding: theme.spacing(0.4),
      borderRadius: theme.spacing(0.5),
      cursor: "pointer",
    },

    tooltip: {
      position: "absolute",
      pointerEvents: "auto",
      display: "flex",
      flexDirection: "column",
      padding: theme.spacing(1),
      backgroundColor: theme.palette.background.paper,
      width: "auto",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.spacing(1.25),
      maxWidth: "max-content",
      minWidth: 250,
      transition: "top 0.25s ease, left 0.25s ease",
    },

    progressFill: {
      height: 12,
      borderRadius: 4,
      marginRight: theme.spacing(1),
    },

    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      mb: 1,
    },
    viewToggle: {
      fontSize: "0.75rem",
      fontWeight: 500,
      color: theme.palette.primary.main,
      cursor: "pointer",
      textDecoration: "underline",
      width: "max-content",
    },
    summaryItem: {
      display: "flex",
      flexDirection: "column",
      mb: 0.5,
    },
    summaryLabel: {
      fontSize: "0.75rem",
      fontWeight: 400,
      color: theme.palette.text.primary,
      lineHeight: 1.3,
    },
    summaryRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
    },
  };
});

export const getTopExercises = (
  exercises: CompletedExercisesWithAssetDto[],
  limit = 5,
) => {
  const grouped = exercises.reduce<Record<number, TopExerciseItem>>(
    (acc, ex) => {
      if (!ex.assetId) return acc;

      if (!acc[ex.assetId]) {
        acc[ex.assetId] = {
          count: 1,
          exercise: new UserExerciseDto({
            id: ex.assetId,
            title: ex.assetName,
            exercise: new ExerciseAssetDto({
              id: ex.assetId,
              name: ex.assetName,
              bodyparts: ex.bodyparts,
              movementPatterns: ex.movementPatterns,
              content: ex.content,
              coachId: ex.coachId,
            }),
          }),
        };
      } else {
        acc[ex.assetId].count++;
      }

      return acc;
    },
    {},
  );

  return Object.values(grouped)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
};

export enum TooltipMode {
  DETAILS = "details",
  SUMMARY = "summary",
}

interface RadarTooltipProps {
  selectedBodypart: RadarDataItem | null;
  tooltipPos: { x: number; y: number } | null;
  tooltipRef: React.RefObject<HTMLDivElement>;
  radarData: RadarDataItem[];
  onExerciseSelect?: (exercise: UserExerciseDto) => void;
  initialMode?: TooltipMode;
  isTouchMode?: boolean;
  onClose?: () => void;
}

export const RadarTooltip: FC<RadarTooltipProps> = ({
  selectedBodypart,
  tooltipPos,
  tooltipRef,
  radarData,
  onExerciseSelect,
  initialMode = TooltipMode.DETAILS,
  isTouchMode = false,
  onClose,
}) => {
  const classes = useTooltipStyles();
  const [mode, setMode] = useState<TooltipMode>(initialMode);

  const theme = useTheme();

  const topExercises = selectedBodypart
    ? getTopExercises(selectedBodypart.relatedExercises)
    : [];

  const tooltipStyle: React.CSSProperties = tooltipPos
    ? {
        position: "absolute",
        top: tooltipPos.y,
        left: tooltipPos.x,
        transform: "translate(-50%, -50%)",
        pointerEvents: "auto",
      }
    : { display: "none" };

  if (!selectedBodypart && mode === TooltipMode.DETAILS) return null;

  const navigateLabel = (
    <Typography
      className={classes.viewToggle}
      onClick={() =>
        setMode(
          mode === TooltipMode.DETAILS
            ? TooltipMode.SUMMARY
            : TooltipMode.DETAILS,
        )
      }
    >
      {mode === TooltipMode.DETAILS ? "See all bodyparts" : "Back to details"}
    </Typography>
  );

  const stopEvent = (e: React.SyntheticEvent) => e.stopPropagation();

  const stopProps = {
    onTouchStart: stopEvent,
    onTouchMove: stopEvent,
    onTouchEnd: stopEvent,
    onClick: stopEvent,
    onMouseEnter: stopEvent,
    onMouseMove: stopEvent,
    onMouseLeave: stopEvent,
    onMouseDown: stopEvent,
  };

  return (
    <Box
      ref={tooltipRef}
      style={tooltipStyle}
      className={classes.tooltip}
      {...stopProps}
    >
      <Box className={classes.header}>
        <Typography variant="body1" fontWeight={600}>
          {mode === TooltipMode.DETAILS && selectedBodypart
            ? capitalize(selectedBodypart.subject)
            : "Bodyparts"}
        </Typography>
        {isTouchMode ? <Close size={20} onClick={onClose} /> : navigateLabel}
      </Box>

      {mode === TooltipMode.DETAILS && selectedBodypart && (
        <>
          <Box display="flex" flexDirection="column">
            <Box display="flex" flexDirection={"row"} alignItems="center">
              <Box
                className={classes.progressFill}
                style={{
                  background: `linear-gradient(to right, ${theme.palette.primary.main} ${selectedBodypart.value}%, ${alpha(theme.palette.primary.main, 0.1)} ${selectedBodypart.value}%)`,
                  width: "50%",
                }}
              />
              <Typography variant="caption" fontWeight={500}>
                {selectedBodypart.value}%
              </Typography>
            </Box>
            {isTouchMode && navigateLabel}
          </Box>
          <Divider sx={{ my: 1, borderColor: "divider" }} />

          {topExercises.length > 0 ? (
            <Box>
              <Typography
                variant="body2"
                fontWeight={600}
                mb={0.5}
                color="text.primary"
              >
                {topExercises.length === 5
                  ? "Top 5 Exercises"
                  : "Top Exercises"}
              </Typography>
              <Box component="ul" className={classes.list}>
                {topExercises.map((item) => (
                  <TooltipExerciseItem
                    key={item.exercise.id}
                    userExercise={item.exercise}
                    count={item.count}
                    onExerciseSelect={onExerciseSelect}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" mt={0.5}>
              No exercises completed for this body part.
            </Typography>
          )}
        </>
      )}

      {mode === TooltipMode.SUMMARY && (
        <Box>
          {isTouchMode && (
            <>
              {navigateLabel}
              <Divider sx={{ my: 1 }} />
            </>
          )}

          {radarData.map((item) => (
            <Box key={item.subject} className={classes.summaryItem}>
              <Typography className={classes.summaryLabel}>
                {capitalize(item.subject)}
              </Typography>
              <Box className={classes.summaryRow}>
                <Box
                  className={classes.progressFill}
                  style={{
                    background: `linear-gradient(to right, ${theme.palette.primary.main} ${item.value}%, ${alpha(theme.palette.primary.main, 0.1)} ${item.value}%)`,
                    width: "100%",
                  }}
                />
                <Typography variant="caption">{item.value}%</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
